import { STEP_MAP } from '@/components/CustomDomainPopup/constants';
import {
  CustomDomainActiveDispatchContext,
  CustomDomainPopupDispatchContext,
} from '@/components/CustomDomainPopup/context';
import {
  CUSTOM_DOMAIN_LEARN_MORE_LINK,
  FIRST_TIME_PUBLISH_TWITTER_POPUP_KEY,
  FIRST_TIME_PUBLISH_WITH_CUSTOM_DOMAIN_TWITTER_POPUP_KEY,
  SPACE_SUBSCRIPTIONS,
} from '@/constants';
import { useRevalidateStatusStore } from '@/data/operation/store';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { useGetSpaceQuery } from '@/store';
import TokenContext from '@/utils/jwt/tokenContext';
import useCurrentPagePath from '@/utils/useCurrentPagePath';
import { ExternalLink } from '@styled-icons/evil/ExternalLink';
import { Bolt } from '@styled-icons/material';
import { DotFill } from '@styled-icons/octicons';
import { SPACE_ACTION } from '@typedream/data';
import { Button, Text, cn } from '@typedream/ui';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { useReward } from 'react-rewards';
import Popup from '../Popup';
import {
  styBgImageHeader,
  styBgTwitterIcon,
  styBigBoxBottomContainer,
  styBigBoxContentContainer,
  styBottomContainer,
  styBoxSiteUrl,
  styButtonBottomContainer,
  styButtonTweet,
  styLink,
  styPopupContentContainer,
  styTextNextStep,
  styTextPopupHeader,
  styTextUrl,
  styTwitterBoxContent,
  styTwitterBoxHeader,
} from './style';

export default function TwitterEncouragementPopup() {
  const router = useRouter();
  const { typedreamSubdomain, customDomain, currentPathWithoutSlug } = useCurrentPagePath();
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const { status } = useRevalidateStatusStore();
  const pathWithoutSlug = `${currentPathWithoutSlug ? `/${currentPathWithoutSlug}` : ''}`;
  const customDomainEndpoint = `${customDomain || typedreamSubdomain}`;
  const siteUrlKey = customDomain || typedreamSubdomain;
  const url = `${customDomainEndpoint}${pathWithoutSlug}`;
  const { reward: publishedReward } = useReward('publishedReward', 'confetti', {
    lifetime: 1000,
  });

  const setActive = useContext(CustomDomainActiveDispatchContext);
  const [isShow, setIsShow] = useState<boolean>(false);
  const setcustomDomainPopupOpen = useContext(CustomDomainPopupDispatchContext);
  const [upgradeRequired, setupgradeRequired] = useState(true);
  const { subscriptionTokenHandler } = useContext(TokenContext);
  const { data: space } = useGetSpaceQuery([spaceID]);
  const checkSubscription = async () => {
    const sub = await subscriptionTokenHandler.getSubscription(spaceID as string);
    setupgradeRequired(!sub.isAllowed(SPACE_ACTION.UpdateCustomDomain, SPACE_SUBSCRIPTIONS));
  };

  const handleTweet = () => {
    const tweetFirstTimePublish = `Just%20built%20a%20landing%20page%20on%20@typedreamHQ%20and%20it’s%20looking%20great%20already!%20What%20do%20you%20think?%20👀%20${url}`;
    const tweetConnectedDomain = `It’s%20ALIVE%20🤩!%20Just%20launched%20my%20landing%20page%20with%20@typedreamHQ:%20${url}%0A%0ARoast%20pls%20👀`;
    const tweet = customDomain ? tweetConnectedDomain : tweetFirstTimePublish;
    window.open(`https://twitter.com/intent/tweet?text=${tweet}`, '_blank');
  };

  const handleAddCustomDomain = () => {
    setIsShow(false);
    if (!upgradeRequired) {
      router.push(`/settings/${space?.slug}/${spaceID}/custom_domain`);
    } else {
      setActive(STEP_MAP.add_custom_domain.key);
      setcustomDomainPopupOpen(true);
    }
  };

  useEffect(() => {
    if (spaceID) {
      checkSubscription();
    }
  }, [spaceID]);

  const succeedEndpoints =
    status?.endpoints &&
    Object.values(status?.endpoints)?.filter((s) => s.status === 'succeeded')?.length;

  useEffect(() => {
    if (localStorage) {
      const firstTimePublishTwitterPopupKey = `${FIRST_TIME_PUBLISH_TWITTER_POPUP_KEY}_${siteUrlKey}`;
      const firstTimePublishTwitterPopupWithCustomDomainKey = `${FIRST_TIME_PUBLISH_WITH_CUSTOM_DOMAIN_TWITTER_POPUP_KEY}_${siteUrlKey}`;
      const shownPopup = localStorage.getItem(firstTimePublishTwitterPopupKey);
      const shownPopupWithCustomDomain = localStorage.getItem(
        firstTimePublishTwitterPopupWithCustomDomainKey
      );
      if (
        succeedEndpoints > 0 &&
        ((!shownPopup && !customDomain) || (!shownPopupWithCustomDomain && customDomain))
      ) {
        setIsShow(true);
        publishedReward();
        if (!shownPopupWithCustomDomain && customDomain) {
          localStorage.setItem(firstTimePublishTwitterPopupWithCustomDomainKey, '1');
          return;
        }
        localStorage.setItem(firstTimePublishTwitterPopupKey, '1');
      }
    }
  }, [succeedEndpoints]);
  const connectedDomain = customDomain;

  return (
    <Popup
      open={isShow}
      setopen={setIsShow}
      width={588}
      height={connectedDomain ? 671 : 749}
      showCloseButton={false}
    >
      <div css={styPopupContentContainer}>
        <div css={styBgImageHeader}>
          <Image
            src={`/images/${connectedDomain ? 'connected-domain' : 'first-time-publish'}-popup.png`}
            width={101}
            height={101}
            alt=""
          />
        </div>
        <div css={styTextPopupHeader}>
          {connectedDomain
            ? 'Open your site on your Custom Domain 🎉'
            : 'You did it! Your site is live 🎉'}
        </div>
        <div css={styBoxSiteUrl}>
          <span id="publishedReward" style={{ zIndex: 999 }} />

          <a href={`https://${url}`} target="_new" className="block w-full m-0">
            <div className="flex gap-1 items-center text-primary-soft hover:text-primary w-[319px]">
              <DotFill
                size={13}
                className={cn('text-2xs align-top', {
                  'text-green-500': true,
                })}
              />
              <Text as="p" weight="medium" size="xl">
                <div css={styTextUrl}>{url}</div>
              </Text>
              <ExternalLink size={24} color="var(--deprecated-neutral-600)" className="ml-auto" />
            </div>
          </a>
        </div>
        <div css={styBigBoxBottomContainer} style={{ height: !!connectedDomain && 167 }}>
          <div css={styBigBoxContentContainer}>
            <div css={styBgTwitterIcon}>
              <Image alt="" src="/images/twitter.svg" width={20} height={20} />
            </div>
            <div style={{ color: '#1A1523' }}>
              <div css={styTwitterBoxHeader}>Let’s share it on Twitter!</div>
              {connectedDomain ? (
                <div css={styTwitterBoxContent} style={{ height: 80 }}>
                  It’s ALIVE 🤩! Just launched my landing page with{' '}
                  <span css={styLink}>@typedreamHQ: {url}</span>
                  <br />
                  <br />
                  Roast pls 👀
                </div>
              ) : (
                <div css={styTwitterBoxContent}>
                  Just built a landing page on <span css={styLink}>@typedreamHQ</span> and it’s
                  looking great already! What do you think? 👀 <br />
                  <span css={styLink}>{url}</span>
                </div>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            css={styButtonTweet}
            onClick={handleTweet}
            onKeyDown={handleTweet}
          >
            Tweet
          </div>
        </div>

        {!connectedDomain && (
          <div css={styBottomContainer}>
            <div css={styTextNextStep}>Next step 👉</div>
            <div css={styButtonBottomContainer}>
              <Button variant="invert" onClick={handleAddCustomDomain}>
                <Bolt size={20} />
                Add custom domain
              </Button>
              <a href={CUSTOM_DOMAIN_LEARN_MORE_LINK} target="_blank" rel="noreferrer">
                <Button variant="ghost">Learn More -{'>'}</Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
}
